import { css } from '@styled-system/css';
import { Folder as FolderIcon, TriangleAlert, Zap } from 'lucide-react';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCredits } from '@/features/credits/useCredits';
import { Button } from '@/shared/components/ui/Button';
import { FormField } from '@/shared/components/ui/FormField';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { useGetUserSearchFoldersQuery } from '@/shared/hooks/queries/generated';

// ============================================================================
// Confirmation d'une relance « zoom » (depuis une niche ou un article) :
// configurer le nom et le dossier avant de lancer, en rendant le coût
// explicite — 1 crédit de recherche produit, comme une création normale.
// ============================================================================

export interface RelaunchSearchModalProps {
  /** Note d'avertissement optionnelle (ex. Trend : périmètre plus large). */
  warningText?: string;
  isOpen: boolean;
  /** Nom proposé (niche ou titre de l'article), modifiable. */
  defaultName: string;
  onClose: () => void;
  /** Lance la création — lève une erreur pour l'afficher dans la popup. */
  onConfirm: (name: string, folderId: string | null) => Promise<void>;
}

const body = css({ display: 'flex', flexDirection: 'column', gap: '5', paddingTop: '4' });

const costBox = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '1',
  paddingY: '4',
  paddingX: '5',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'md',
});

const costLine = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
  fontSize: 'caption',
  color: 'text.secondary',
  fontWeight: 'medium',
});

const errorText = css({ fontSize: 'caption', color: 'error.default' });

const warnNote = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '2.5',
  padding: '3',
  borderRadius: 'lg',
  bg: 'warning.soft',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'warning.border',
  color: 'warning.default',
  fontSize: 'caption',
  lineHeight: 1.5,
});

export const RelaunchSearchModal: FC<RelaunchSearchModalProps> = ({
  isOpen,
  defaultName,
  onClose,
  onConfirm,
  warningText,
}) => {
  const { t } = useTranslation();
  const credits = useCredits();
  const [name, setName] = useState(defaultName);
  const [folderId, setFolderId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);

  // Dossiers de recherches produit (une relance zoom est toujours un catalog)
  const { data: foldersData } = useGetUserSearchFoldersQuery({ type: 'catalog' }, { enabled: isOpen });
  const folderOptions = useMemo(
    () => [
      { label: t('relaunch.noFolder'), value: '__none__' },
      ...[...(foldersData?.getUserSearchFolders ?? [])]
        .sort((a, b) => a.position - b.position)
        .map((folder) => ({ label: folder.name, value: folder.id })),
    ],
    [foldersData, t],
  );

  useEffect(() => {
    if (isOpen) {
      setName(defaultName);
      setFolderId(null);
      setError(null);
      setLaunching(false);
    }
  }, [isOpen, defaultName]);

  // Ne bloquer que sur une donnée serveur chargée (jamais sur l'état loading)
  const exhausted = credits.ready && credits.remaining.product === 0;

  const handleConfirm = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('folders.errorRequired'));
      return;
    }
    setLaunching(true);
    try {
      await onConfirm(trimmed, folderId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setLaunching(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('relaunch.title')}
      width="440px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={launching}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" handleClick={handleConfirm} showLoader disabled={exhausted}>
            {t('relaunch.confirm')}
          </Button>
        </>
      }
    >
      <div className={body}>
        {warningText && (
          <div className={warnNote}>
            <TriangleAlert size={13} strokeWidth={1.75} className={css({ flexShrink: 0, marginTop: '0.5' })} />
            <span>{warningText}</span>
          </div>
        )}
        <FormField label={t('relaunch.nameLabel')}>
          <Input
            autoFocus
            value={name}
            state={error ? 'error' : 'default'}
            onChange={(event) => {
              setName(event.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleConfirm();
              }
            }}
          />
        </FormField>

        {/* Rangement direct dans un dossier de l'historique (optionnel) */}
        <FormField label={t('relaunch.folderLabel')} icon={<FolderIcon size={14} strokeWidth={1.75} />}>
          <Select
            value={folderId ?? '__none__'}
            onChange={(value) => setFolderId(value === '__none__' ? null : value)}
            options={folderOptions}
          />
        </FormField>

        {/* Coût — mêmes règles qu'une création : 1 crédit produit, débité à
            la création (pas de re-crédit à la suppression) */}
        <div className={costBox}>
          {exhausted ? (
            <span className={css({ fontSize: 'caption', color: 'error.default', fontWeight: 'medium' })}>
              {t('relaunch.exhausted', { max: credits.limits.product })}
            </span>
          ) : (
            <span className={costLine}>
              <Zap size={12} strokeWidth={2} fill="currentColor" />
              {t('relaunch.cost', { count: credits.remaining.product - 1 })}
            </span>
          )}
        </div>

        {error && <span className={errorText}>{error}</span>}
      </div>
    </Modal>
  );
};
